import { useState, useEffect, useCallback } from 'react'
import { salespersonClientAPI } from '../../services/api'
import ContentCalendarPage from '../user/ContentCalendarPage'

const CAPTION_LENGTH_OPTIONS = [
  { value: 'SHORT', label: '짧게', desc: '1-2문장' },
  { value: 'MEDIUM', label: '보통', desc: '2-4문장' },
  { value: 'LONG', label: '길게', desc: '4-6문장' },
]

const CTA_OPTIONS = [
  { value: 'NONE', label: '없음', icon: 'block' },
  { value: 'LIKE', label: '좋아요', icon: 'favorite' },
  { value: 'FOLLOW', label: '팔로우', icon: 'person_add' },
  { value: 'COMMENT', label: '댓글', icon: 'chat' },
  { value: 'SAVE', label: '저장', icon: 'bookmark' },
  { value: 'LINK', label: '링크', icon: 'link' },
]

const EMOJI_DENSITY_OPTIONS = [
  { value: 'NONE', label: '없음' },
  { value: 'LOW', label: '적음' },
  { value: 'MEDIUM', label: '보통' },
  { value: 'HIGH', label: '많음' },
]

const IMAGE_STYLE_OPTIONS = [
  { value: 'PHOTO', label: '사진', icon: 'photo_camera' },
  { value: 'FLAT_LAY', label: '플랫레이', icon: 'grid_view' },
  { value: 'CLOSE_UP', label: '클로즈업', icon: 'zoom_in' },
  { value: 'EDITORIAL', label: '에디토리얼', icon: 'auto_awesome' },
  { value: 'LIFESTYLE', label: '라이프스타일', icon: 'self_improvement' },
  { value: 'MINIMAL_GRAPHIC', label: '미니멀', icon: 'palette' },
]

const DAYS_OF_WEEK = [
  { value: 1, label: '월' },
  { value: 2, label: '화' },
  { value: 3, label: '수' },
  { value: 4, label: '목' },
  { value: 5, label: '금' },
  { value: 6, label: '토' },
  { value: 7, label: '일' },
]

export default function ClientManagementPage() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedClient, setSelectedClient] = useState(null)
  const [activeDetailTab, setActiveDetailTab] = useState('content')
  const [contentSettings, setContentSettings] = useState(null)
  const [schedule, setSchedule] = useState(null)
  const [posts, setPosts] = useState([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')

  const loadClients = useCallback(async () => {
    try {
      setLoading(true)
      const res = await salespersonClientAPI.getClients()
      setClients(res.data || [])
    } catch (err) {
      console.error('고객 목록 로딩 실패:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadClients() }, [loadClients])

  const openClientDetail = async (client) => {
    setSelectedClient(client)
    setActiveDetailTab('content')
    setDetailLoading(true)
    setSaveMessage('')
    try {
      const [contentRes, scheduleRes] = await Promise.all([
        salespersonClientAPI.getContentSettings(client.accountId),
        salespersonClientAPI.getSchedule(client.accountId),
      ])
      setContentSettings(contentRes.data || {})
      setSchedule(scheduleRes.data || {})
    } catch (err) {
      console.error('상세 로딩 실패:', err)
    } finally {
      setDetailLoading(false)
    }
  }

  const loadPosts = async (accountId) => {
    try {
      const res = await salespersonClientAPI.getPosts(accountId)
      setPosts(res.data || [])
    } catch (err) {
      console.error('게시물 로딩 실패:', err)
    }
  }

  const handleTabChange = (tab) => {
    setActiveDetailTab(tab)
  }

  const updateContentField = (field, value) => {
    setContentSettings(prev => ({ ...prev, [field]: value }))
  }

  const saveContentSettings = async () => {
    if (!selectedClient) return
    setSaving(true)
    setSaveMessage('')
    try {
      const res = await salespersonClientAPI.updateContentSettings(selectedClient.accountId, contentSettings)
      setContentSettings(res.data)
      setSaveMessage('콘텐츠 설정이 저장되었습니다.')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (err) {
      setSaveMessage('저장 실패: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const saveSchedule = async () => {
    if (!selectedClient) return
    setSaving(true)
    setSaveMessage('')
    try {
      const res = await salespersonClientAPI.updateSchedule(selectedClient.accountId, schedule)
      setSchedule(res.data)
      setSaveMessage('스케줄이 저장되었습니다.')
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (err) {
      setSaveMessage('저장 실패: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const closeDetail = () => {
    setSelectedClient(null)
    setContentSettings(null)
    setSchedule(null)
    setPosts([])
    setSaveMessage('')
  }

  // ===== 고객 목록 화면 =====
  if (!selectedClient) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">고객 관리</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">담당 고객의 자동화 설정과 캘린더를 관리합니다</p>
          </div>
          <button onClick={loadClients} className="flex items-center gap-1 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700">
            <span className="material-symbols-outlined text-[18px]">refresh</span>
            새로고침
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-20">
            <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 mb-4">group_off</span>
            <h2 className="text-xl font-bold text-slate-500 dark:text-slate-400">담당 고객이 없습니다</h2>
            <p className="text-slate-400 dark:text-slate-500 mt-2">추천 코드로 가입한 고객이 여기에 표시됩니다</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map(client => (
              <button
                key={client.accountId}
                onClick={() => openClientDetail(client)}
                className="text-left bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-primary/50 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                      {client.username?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">@{client.username}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{client.userName || '이름 없음'}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    client.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {client.status === 'ACTIVE' ? '활성' : client.status || '대기'}
                  </span>
                </div>

                <div className="space-y-1.5 text-xs text-slate-500 dark:text-slate-400">
                  {client.userPhone && (
                    <div className="flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[14px]">phone</span>
                      {client.userPhone}
                    </div>
                  )}
                  {client.userEmail && (
                    <div className="flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[14px]">mail</span>
                      {client.userEmail}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[14px]">upload</span>
                    자동 업로드: {client.autoUploadEnabled ? '활성' : '비활성'}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                  <span className="text-xs text-slate-400">일일 제한: {client.dailyPostLimit || 25}개</span>
                  <span className="material-symbols-outlined text-[16px] text-primary">arrow_forward</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ===== 고객 상세 화면 =====
  const detailTabs = [
    { id: 'content', label: '콘텐츠 설정', icon: 'tune' },
    { id: 'schedule', label: '스케줄', icon: 'schedule' },
    { id: 'calendar', label: '캘린더', icon: 'calendar_month' },
  ]

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={closeDetail} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div className="flex items-center gap-3">
          <div className="size-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
            {selectedClient.username?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <h1 className="text-xl font-bold">@{selectedClient.username}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">{selectedClient.userName}</p>
          </div>
        </div>
      </div>

      {/* 탭 */}
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 mb-6">
        {detailTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors ${
              activeDetailTab === tab.id
                ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* 저장 메시지 */}
      {saveMessage && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${
          saveMessage.includes('실패') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
        }`}>
          {saveMessage}
        </div>
      )}

      {detailLoading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* 콘텐츠 설정 탭 */}
          {activeDetailTab === 'content' && contentSettings && (
            <div className="space-y-6">
              {/* 기본 정보 */}
              <Section title="기본 정보" icon="info">
                <InputField label="비즈니스 이름" value={contentSettings.businessName} onChange={v => updateContentField('businessName', v)} />
                <InputField label="홍보 목표" value={contentSettings.promotionGoal} onChange={v => updateContentField('promotionGoal', v)} />
                <InputField label="포스트 스타일" value={contentSettings.postStyle} onChange={v => updateContentField('postStyle', v)} />
              </Section>

              {/* 타겟 설정 */}
              <Section title="타겟 설정" icon="target">
                <InputField label="카테고리" value={contentSettings.accountCategory} onChange={v => updateContentField('accountCategory', v)} />
                <InputField label="세부 카테고리" value={contentSettings.accountSubCategory} onChange={v => updateContentField('accountSubCategory', v)} />
                <InputField label="타겟 연령대" value={contentSettings.targetAgeGroup} onChange={v => updateContentField('targetAgeGroup', v)} />
                <InputField label="타겟 성별" value={contentSettings.targetGender} onChange={v => updateContentField('targetGender', v)} />
                <InputField label="타겟 관심사" value={contentSettings.targetInterest} onChange={v => updateContentField('targetInterest', v)} />
                <InputField label="톤 & 매너" value={contentSettings.toneAndManner} onChange={v => updateContentField('toneAndManner', v)} />
              </Section>

              {/* 비주얼 */}
              <Section title="비주얼 가이드" icon="palette">
                <InputField label="브랜드 컬러" value={contentSettings.brandColor} onChange={v => updateContentField('brandColor', v)} />
                <InputField label="이미지 분위기" value={contentSettings.imageMood} onChange={v => updateContentField('imageMood', v)} />
              </Section>

              {/* 게시글 기본 양식 */}
              <Section title="게시글 기본 양식" icon="article">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">기본 캡션</label>
                  <textarea
                    value={contentSettings.defaultCaption || ''}
                    onChange={e => updateContentField('defaultCaption', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <InputField label="고정 해시태그" value={contentSettings.permanentHashtags} onChange={v => updateContentField('permanentHashtags', v)} />
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">포스트당 이미지 수</label>
                  <input
                    type="number" min={1} max={10}
                    value={contentSettings.imagesPerPost || 1}
                    onChange={e => updateContentField('imagesPerPost', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </Section>

              {/* 퀄리티 설정 */}
              <Section title="콘텐츠 퀄리티 설정" icon="high_quality">
                <div className="col-span-2 space-y-4">
                  <OptionGroup
                    label="캡션 길이"
                    options={CAPTION_LENGTH_OPTIONS}
                    value={contentSettings.captionLength || 'MEDIUM'}
                    onChange={v => updateContentField('captionLength', v)}
                  />
                  <OptionGroup
                    label="CTA (행동 유도)"
                    options={CTA_OPTIONS}
                    value={contentSettings.ctaType || 'NONE'}
                    onChange={v => updateContentField('ctaType', v)}
                    useIcon
                  />
                  <OptionGroup
                    label="이모지 밀도"
                    options={EMOJI_DENSITY_OPTIONS}
                    value={contentSettings.emojiDensity || 'MEDIUM'}
                    onChange={v => updateContentField('emojiDensity', v)}
                  />
                  <OptionGroup
                    label="이미지 스타일"
                    options={IMAGE_STYLE_OPTIONS}
                    value={contentSettings.imageStyle || 'PHOTO'}
                    onChange={v => updateContentField('imageStyle', v)}
                    useIcon
                  />
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                      최소 품질 점수: {contentSettings.minQualityScore || 0}
                      {(contentSettings.minQualityScore || 0) === 0 && ' (비활성)'}
                    </label>
                    <input
                      type="range" min={0} max={10}
                      value={contentSettings.minQualityScore || 0}
                      onChange={e => updateContentField('minQualityScore', parseInt(e.target.value))}
                      className="w-full accent-primary"
                    />
                    <div className="flex justify-between text-[10px] text-slate-400">
                      <span>비활성</span><span>10</span>
                    </div>
                  </div>
                </div>
              </Section>

              {/* 저장 버튼 */}
              <div className="flex justify-end">
                <button
                  onClick={saveContentSettings}
                  disabled={saving}
                  className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium text-sm hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? '저장 중...' : '콘텐츠 설정 저장'}
                </button>
              </div>
            </div>
          )}

          {/* 스케줄 탭 */}
          {activeDetailTab === 'schedule' && schedule && (
            <div className="space-y-6">
              <Section title="자동 업로드 설정" icon="schedule">
                <div className="col-span-2 space-y-4">
                  {/* 자동 업로드 토글 */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">자동 업로드</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">설정된 시간에 자동으로 게시합니다</p>
                    </div>
                    <button
                      onClick={() => setSchedule(prev => ({ ...prev, autoUploadEnabled: !prev.autoUploadEnabled }))}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        schedule.autoUploadEnabled ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    >
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                        schedule.autoUploadEnabled ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  {/* 업로드 요일 */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">업로드 요일</label>
                    <div className="flex gap-2">
                      {DAYS_OF_WEEK.map(day => {
                        const selected = (schedule.daysOfWeek || []).includes(day.value)
                        return (
                          <button
                            key={day.value}
                            onClick={() => {
                              const current = schedule.daysOfWeek || []
                              setSchedule(prev => ({
                                ...prev,
                                daysOfWeek: selected
                                  ? current.filter(d => d !== day.value)
                                  : [...current, day.value].sort()
                              }))
                            }}
                            className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                              selected
                                ? 'bg-primary text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                            }`}
                          >
                            {day.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* 업로드 시간 */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">업로드 시간</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {(schedule.uploadTimes || []).map((time, idx) => (
                        <div key={idx} className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                          {time}
                          <button
                            onClick={() => setSchedule(prev => ({
                              ...prev,
                              uploadTimes: prev.uploadTimes.filter((_, i) => i !== idx)
                            }))}
                            className="hover:text-red-500"
                          >
                            <span className="material-symbols-outlined text-[14px]">close</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <input
                      type="time"
                      onBlur={e => {
                        const val = e.target.value
                        if (val && !(schedule.uploadTimes || []).includes(val)) {
                          setSchedule(prev => ({
                            ...prev,
                            uploadTimes: [...(prev.uploadTimes || []), val].sort()
                          }))
                          e.target.value = ''
                        }
                      }}
                      className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
                    />
                  </div>

                  {/* 일일 포스트 제한 */}
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">일일 포스트 제한</label>
                    <input
                      type="number" min={1} max={100}
                      value={schedule.dailyPostLimit || 25}
                      onChange={e => setSchedule(prev => ({ ...prev, dailyPostLimit: parseInt(e.target.value) || 25 }))}
                      className="w-32 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
                    />
                  </div>
                </div>
              </Section>

              <div className="flex justify-end">
                <button
                  onClick={saveSchedule}
                  disabled={saving}
                  className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium text-sm hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? '저장 중...' : '스케줄 저장'}
                </button>
              </div>
            </div>
          )}

          {/* 캘린더 탭 */}
          {activeDetailTab === 'calendar' && (
            <ContentCalendarPage
              accountId={selectedClient.accountId}
              embedded={true}
              apiOverride={{
                list: (accountId) => salespersonClientAPI.getCalendars(accountId),
                getById: (id) => salespersonClientAPI.getCalendar(selectedClient.accountId, id),
                updatePost: (calId, postId, data) =>
                  salespersonClientAPI.updateCalendarPost(selectedClient.accountId, calId, postId, data),
                deletePost: (calId, postId) =>
                  salespersonClientAPI.deleteCalendarPost(selectedClient.accountId, calId, postId),
              }}
              readOnly={true}
            />
          )}
        </>
      )}
    </div>
  )
}

// ===== 공통 컴포넌트 =====

function Section({ title, icon, children }) {
  return (
    <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="material-symbols-outlined text-[20px] text-primary">{icon}</span>
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  )
}

function InputField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary/20 focus:border-primary"
      />
    </div>
  )
}

function OptionGroup({ label, options, value, onChange, useIcon }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              value === opt.value
                ? 'bg-primary text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            {useIcon && opt.icon && <span className="material-symbols-outlined text-[14px]">{opt.icon}</span>}
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}
